/**
 * The class (notes) screen, the flashcard screen, the sidebar and the agenda.
 *
 * CLASSES is rebuilt from Canvas on sign-in and on every refresh (see
 * loadclasses.js); notes and flashcards still live only in that in-memory
 * array, so editing one writes back onto the sample content and a reload
 * restores it. The agenda panel fetches from the Canvas Lambda through
 * canvas.js and renders whatever it gets back — there is no local fallback.
 */
import { CLASSES } from './classes.js';
import * as study from './study.js';
import * as ui from './ui.js';
import { scrollDistance, scrollSeconds } from './marquee.js';
import { rangeFor, groupByDay, withinRange, isoDay } from './agenda.js';
import { assignments as fetchAssignments, describeCanvasError } from './canvas.js';
import { windowFor, groupEvents, startLabel, endLabel } from './calendar.js';
import { loadAllEvents } from './gcal.js';
import { indexByEvent, loadDayPlan, loadDayPlans } from './dayplan.js';
import { mergeToday } from './today.js';
import { onCanvasSubmitted, tickOff, untick } from './agreements.js';
import { clearDone, endSession, listDone, markDone } from './sessions.js';
import { liveSession, openTimerFor, refreshLiveSession } from './timerscreen.js';
import { NOT_CONFIGURED } from './gauth.js';

const SWIPE_PX = 90; // drag at least this far to score the card
const TAP_PX = 8; // move less than this and it was a tap, which flips
const LEAVE_MS = 200; // must match .card-swipe.is-leaving in styles.css
const TILT_DEG_PER_PX = 0.05; // the card leans into the direction it is thrown

let classIndex = 0;
let setIndex = 0;
let noteIndex = 0;
let session = null;

const drag = { active: false, startX: 0, dx: 0, leaving: false };

const reducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const currentClass = () => CLASSES[classIndex];
const currentSet = () => currentClass().sets[setIndex];

/**
 * Build the sidebar's class rows from whatever Canvas last said.
 *
 * Safe to call again: it clears its own rows first, which is what makes a
 * refresh after a slow Canvas reply work rather than duplicating everything.
 */
export function mountSidebar() {
  const nav = ui.el('side-nav');
  for (const stale of nav.querySelectorAll('[data-class]')) stale.remove();

  CLASSES.forEach((cls, index) => {
    const row = ui.clone('tpl-class-nav');
    row.dataset.nav = `class-${index}`;
    row.dataset.class = String(index);
    row.querySelector('.nav-dot').style.background = cls.color;
    row.querySelector('.nav-label-text').textContent = cls.name;

    if (cls.unread > 0) {
      const badge = document.createElement('span');
      badge.className = 'nav-badge';
      badge.textContent = `${cls.unread} NEW`;
      row.append(badge);
    }
    nav.append(row);
  });
  measureLabels();
}

/**
 * Which labels are actually clipped, and how far they have to travel.
 *
 * Runs after the rows are in the document, because nothing has a width
 * before that. Safe to run again: it recomputes from scratch, which is what
 * makes it correct after a window resize as well as after a Canvas refresh.
 */
export function measureLabels() {
  for (const label of document.querySelectorAll('#side-nav .nav-label')) {
    const text = label.querySelector('.nav-label-text');
    const distance = scrollDistance(text.scrollWidth, label.clientWidth);

    label.classList.toggle('is-clipped', distance > 0);
    label.style.setProperty('--nav-scroll-x', `${-distance}px`);
    label.style.setProperty('--nav-scroll-s', `${scrollSeconds(distance)}s`);
  }
}

/* ── what is due ───────────────────────────────────────────── */

let agendaSource = null;
let agendaView = 'today';
// Bumped on every showAgenda call and on clear, so a reply that lands after a
// newer one (or after sign-out) can tell it is stale and no-op instead of
// overwriting the screen with someone else's answer.
let agendaRequestId = 0;

const EMPTY = {
  today: 'Nothing due today.',
  week: 'Nothing due in the next seven days.',
  month: 'Nothing due in the next thirty days.',
};

/** The site hands over its credentials once, after sign-in. */
export function setAgendaSource(config, session) {
  agendaSource = config && session ? { config, session } : null;
  // Signing out must not leave the next person looking at this one's list --
  // the panel owns its own reset so nothing calling this can forget it.
  if (!agendaSource) clearAgenda();
}

/** Empty the panel and put it back the way it looked before anyone signed in. */
function clearAgenda() {
  agendaRequestId += 1; // outruns any fetch already in flight for the old source
  agendaView = 'today';
  for (const button of document.querySelectorAll('#agenda-views .seg-btn')) {
    const selected = button.dataset.view === 'today';
    button.classList.toggle('is-on', selected);
    button.setAttribute('aria-selected', String(selected));
  }
  ui.el('agenda-days').replaceChildren();
  ui.el('agenda-empty').textContent = EMPTY.today;
  ui.el('agenda-empty').hidden = false;
}

export async function showAgenda(view = agendaView) {
  agendaView = view;
  for (const button of document.querySelectorAll('#agenda-views .seg-btn')) {
    // aria-selected alongside is-on: colour alone does not tell a screen
    // reader which tab is active.
    const selected = button.dataset.view === view;
    button.classList.toggle('is-on', selected);
    button.setAttribute('aria-selected', String(selected));
  }
  if (!agendaSource) return;

  const requestId = ++agendaRequestId;
  const range = rangeFor(view, new Date());
  let due;
  try {
    due = await fetchAssignments(agendaSource.config, agendaSource.session, range);
  } catch (error) {
    if (requestId !== agendaRequestId) return; // superseded by a later click or a sign-out
    renderAgenda([]);
    // The same honest message the sidebar uses: an expired Canvas token gets
    // no retry, because the fix is on a machine that is not this one.
    ui.el('agenda-empty').textContent = describeCanvasError(error).message;
    return;
  }

  if (requestId !== agendaRequestId) return; // ditto, for the reply that actually came back
  // The Lambda's own window is padded a day on each side of the local range it
  // was asked for (its compare is against Canvas's UTC timestamps), so this is
  // the filter that turns that generous reply back into exactly what was asked.
  renderAgenda(groupByDay(withinRange(due, range), new Date()));
  ui.el('agenda-empty').textContent = EMPTY[view] ?? EMPTY.today;
}

function renderAgenda(days) {
  const host = ui.el('agenda-days');
  host.replaceChildren();
  ui.el('agenda-empty').hidden = days.length > 0;

  for (const day of days) {
    const block = ui.clone('tpl-agenda-day');
    block.querySelector('.agenda-label').textContent = day.label;
    const list = block.querySelector('.agenda-list');

    for (const item of day.items) {
      const row = ui.clone('tpl-agenda-item');
      const cls = CLASSES.find((c) => String(c.id) === String(item.course_id));

      row.querySelector('.agenda-dot').style.background = cls ? cls.color : 'var(--faint)';
      row.querySelector('.agenda-title').textContent = item.title;
      row.querySelector('.agenda-meta').textContent = metaFor(item, cls);
      row.classList.toggle('is-done', Boolean(item.submitted));

      // What the timer needs to know, carried on the element rather than in a
      // closure, because the rows are re-rendered on every Canvas refresh and
      // the click is delegated to the container.
      const start = row.querySelector('.agenda-start');
      start.dataset.workItem = item.id == null ? '' : `canvas:${item.id}`;
      start.dataset.reason = item.title;

      const link = row.querySelector('.agenda-link');
      // The URL is Canvas's, not ours -- a javascript: link would run on
      // click, and rel="noopener noreferrer" does nothing against that. Only
      // an absolute http(s) URL is trusted with an href; anything else,
      // relative included, is dropped rather than rewritten.
      if (isSafeUrl(item.url)) link.href = item.url;
      else link.removeAttribute('href');

      list.append(row);
    }
    host.append(block);
  }
}

/** Would a browser fetch or navigate to this, rather than run it? */
function isSafeUrl(url) {
  if (!url) return false;
  try {
    const protocol = new URL(url).protocol;
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false; // relative, or not a URL at all
  }
}

function metaFor(item, cls) {
  const code = cls?.meta || '';
  const at = timeOf(item.due);
  if (item.submitted) return code ? `${code} · done` : 'done';
  return [code, at].filter(Boolean).join(' · ');
}

/** Canvas due times are UTC; the student is not. */
function timeOf(due) {
  const when = new Date(due);
  if (Number.isNaN(when.getTime())) return '';
  return when.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/* ── today, on the screen you open first ───────────────────── */

/**
 * One list of what today actually holds, merged from the plan Alter
 * published, Canvas's due list, and the calendar.
 *
 * The merge rules all live in `today.js` and are tested there without a DOM.
 * What lives here is the part that cannot be pure: gathering three sources
 * that fail independently, and drawing the result.
 *
 * Tolerance is the whole design. Canvas expires, Google disconnects, and the
 * desktop may not have published a plan for today -- each of those is an
 * ordinary state, not an error, and none of them may cost the other two. So
 * every source is read into an empty default and the list is drawn from
 * whatever came back. A day where only the calendar answered still shows the
 * calendar.
 */
let todaySource = null;
// Same guard the agenda and calendar keep: a reply that lands after a newer
// request, or after a sign-out, must not overwrite the screen.
let todayRequestId = 0;
let todayDoneIds = new Set();

export function setTodaySource(config, session) {
  todaySource = config && session ? { config, session } : null;
  if (!todaySource) clearToday();
}

function clearToday() {
  todayRequestId += 1;
  todayDoneIds = new Set();
  ui.el('today-list').replaceChildren();
  ui.el('today-empty').hidden = false;
}

/** `YYYY-MM-DD` for the local day, which is the day the plan was filed under. */
function todayIso(now = new Date()) {
  return isoDay(now);
}

async function planFor(dateIso) {
  try {
    return await loadDayPlan(todaySource.config, todaySource.session, dateIso);
  } catch {
    return { blocks: [] };
  }
}

async function assignmentsToday(range) {
  try {
    const due = await fetchAssignments(todaySource.config, todaySource.session, range);
    return withinRange(due, range);
  } catch {
    // Canvas being expired is said in the Due panel, which is the panel that
    // can explain it. Saying it twice on one screen is noise.
    return [];
  }
}

async function eventsToday(now) {
  if (!calendarAccess?.connected) return [];
  try {
    const result = await readCalendar(windowFor('day', now));
    return result.events.filter((event) => event.startDay === isoDay(now));
  } catch {
    return [];
  }
}

export async function showToday() {
  if (!todaySource) return;

  const requestId = ++todayRequestId;
  const now = new Date();
  const dateIso = todayIso(now);
  const range = { start: dateIso, end: dateIso };

  // In parallel, and none of them able to reject: four sequential awaits
  // would make the slowest source the cost of the whole list.
  const [plan, assignments, events, done] = await Promise.all([
    planFor(dateIso), assignmentsToday(range), eventsToday(now), doneFlags(),
  ]);

  if (requestId !== todayRequestId) return;
  todayDoneIds = done;
  const rows = mergeToday({ blocks: plan.blocks, assignments, events, doneIds: done });
  renderToday(rows);
  // Drawn first, then reconciled: an assignment Canvas calls submitted
  // already renders ticked, so making the screen wait for a write it does not
  // need would be latency for nothing.
  if (await recordCanvasSubmissions(rows, assignments)) {
    // A BLOCK for submitted work is the case that does need the redraw. Its
    // row is keyed w118 and knows nothing about the submission, so without
    // this it stays unticked until the next reload -- the one visible half of
    // "Canvas says submitted" quietly not happening.
    if (requestId !== todayRequestId) return;
    renderToday(mergeToday({ blocks: plan.blocks, assignments, events, doneIds: todayDoneIds }));
  }
}

async function doneFlags() {
  try {
    // From the beginning of time: there are only ever as many flags as there
    // are things on a day's list, and a watermark the page cannot persist
    // across a reload would be one more thing to get wrong.
    const flags = await listDone(todaySource.config, todaySource.session, 0);
    return new Set(flags.map((flag) => flag.itemKey));
  } catch {
    // Nothing ticked is a worse answer than the truth, but it is a safe one:
    // the row is still there and still tappable.
    return new Set();
  }
}

/**
 * The third agreement: Canvas noticing a submission ticks the item off here.
 *
 * Only for assignments not already flagged, so this is a no-op on every load
 * after the first. `source: 'canvas'` is what makes the record able to say
 * later that Canvas knew rather than that somebody tapped.
 */
async function recordCanvasSubmissions(rows, assignments) {
  const submitted = new Set(
    assignments.filter((a) => a.submitted && a.id != null).map((a) => `c${a.id}`),
  );
  if (submitted.size === 0) return false;

  let changed = false;

  const live = liveSession();
  for (const row of rows) {
    const canvasKey = row.canvasAssignmentId ? `c${row.canvasAssignmentId}` : null;
    const key = row.key;
    if (!key || !canvasKey || !submitted.has(canvasKey)) continue;
    if (todayDoneIds.has(key) || todayDoneIds.has(canvasKey)) continue;

    await onCanvasSubmitted(
      { markDone, endSession }, todaySource.config, todaySource.session,
      { key, canvasKey, live },
    );
    todayDoneIds.add(key);
    changed = true;
  }
  return changed;
}

/**
 * A thumb on a checkbox.
 *
 * The row is redrawn immediately and the writes happen behind it: a checkbox
 * that waits for a round trip before it looks pressed feels broken on a
 * phone, and both writes are safe to lose -- the next load asks again.
 */
export async function onTodayCheck(event) {
  const button = event.target.closest('.today-check');
  if (!button || !todaySource) return;

  const key = button.dataset.key;
  if (!key) return;
  const wasDone = button.getAttribute('aria-checked') === 'true';
  const canvasKey = button.dataset.canvasKey || null;

  button.setAttribute('aria-checked', String(!wasDone));
  button.closest('.today-item')?.classList.toggle('is-done', !wasDone);
  if (wasDone) todayDoneIds.delete(key); else todayDoneIds.add(key);

  const deps = { markDone, clearDone, endSession };
  if (wasDone) {
    await untick(deps, todaySource.config, todaySource.session, { key });
    return;
  }

  // The first agreement: ticking an item ends the timer running against it.
  const { ended } = await tickOff(deps, todaySource.config, todaySource.session, {
    key, canvasKey, live: liveSession(),
  });
  // The pill is the visible half of that agreement. Without this it keeps
  // counting until the next heartbeat, which reads as the tick not working.
  if (ended) await refreshLiveSession();
}

function renderToday(rows) {
  const host = ui.el('today-list');
  host.replaceChildren();
  ui.el('today-empty').hidden = rows.length > 0;

  for (const item of rows) {
    const row = ui.clone('tpl-today-item');
    row.classList.toggle('is-done', item.done);

    const check = row.querySelector('.today-check');
    // A real button with a checkbox role, not a styled div: a div cannot be
    // reached by keyboard and announces nothing about its state.
    check.setAttribute('aria-checked', String(item.done));
    check.setAttribute('aria-label', `${item.done ? 'Undo' : 'Done'}: ${item.title}`);
    check.dataset.key = item.key ?? '';
    check.dataset.workItem = item.workItemId == null ? '' : String(item.workItemId);
    // Carried so ticking a block can also end a timer that was started from
    // the agenda against the same Canvas assignment -- the same work, timed
    // under one spelling and ticked under the other.
    check.dataset.canvasKey = item.canvasAssignmentId ? `c${item.canvasAssignmentId}` : '';

    row.querySelector('.today-title').textContent = item.title;
    row.querySelector('.today-meta').textContent = todayMeta(item);
    host.append(row);
  }
}

/**
 * The one line under a title: when it happens, and for a block how long it was
 * meant to take.
 *
 * A block says "planned", never "submitted" or anything Canvas-shaped, for
 * the same reason the done-flag is worded that way -- nothing here can know
 * what Canvas thinks, and a word that implies otherwise would be a lie the
 * screen tells on Alter's behalf.
 */
function todayMeta(item) {
  const at = item.allDay
    ? 'All day'
    : new Date(item.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const parts = [item.courseCode, at];
  if (item.kind === 'block' && item.plannedMinutes) parts.push(`${item.plannedMinutes} min planned`);
  if (item.kind === 'assignment') parts.push('due');
  return parts.filter(Boolean).join(' · ');
}

/* ── google calendar, read only ────────────────────────────── */

/**
 * The screen only ever reads. There is no row here that can be tapped into an
 * edit, because the grant behind it is `calendar.readonly` and could not carry
 * one out if there were.
 *
 * `access` is handed over by main.js and is the whole of what this screen
 * knows about Google: whether a client id exists at all, how to get a token
 * (silently when it can), how to forget one, and how to give it back.
 */
let calendarAccess = null;
let calendarView = 'day';
// Calendar event id -> the study block it is. Alter created those events and
// stored the id against the session, so this is the one trustworthy way to
// know an event on the calendar IS a piece of planned work -- and, crucially,
// WHICH piece, which is what a timer needs. Rebuilt on every refresh.
let calendarBlocks = new Map();
// Same guard the agenda uses: a reply that lands after a newer request, or
// after a disconnect, must not overwrite the screen.
let calendarRequestId = 0;

const CALENDAR_EMPTY = {
  day: 'Nothing scheduled today.',
  week: 'Nothing scheduled in the next seven days.',
};

export function setCalendarAccess(access) {
  calendarAccess = access;
  calendarView = 'day';
  calendarRequestId += 1; // outruns anything already in flight for the old access
  markCalendarView(calendarView);
  showConnected(Boolean(access?.connected));
  ui.el('cal-days').replaceChildren();
  ui.el('cal-sources').replaceChildren();
  calStatus('', null);
  paintConnectPanel();
}

/** Which of the two states the screen is in, and what the header says. */
function showConnected(connected) {
  ui.el('cal-panel').hidden = !connected;
  ui.el('cal-connect').hidden = connected;
  ui.el('cal-account').hidden = !connected;
}

function paintConnectPanel() {
  const configured = Boolean(calendarAccess?.configured);
  const note = ui.el('cal-connect-note');
  const button = ui.el('cal-connect-btn');

  // An unconfigured site says so. The alternative is a calendar with nothing
  // in it, which looks exactly like an empty life rather than a missing setup.
  note.textContent = configured
    ? 'This is a second sign-in, separate from Alter. Google will ask you to allow '
      + 'read-only access to your calendars. Alter cannot create, change or delete '
      + 'anything with it.'
    : NOT_CONFIGURED;
  button.disabled = !configured;
  connectError('');
}

function connectError(message) {
  const node = ui.el('cal-connect-error');
  node.textContent = message;
  node.hidden = !message;
}

function calStatus(message, kind) {
  const node = ui.el('cal-status');
  node.textContent = message;
  node.className = kind ? `status is-${kind}` : 'status';
  node.hidden = !message;
}

function markCalendarView(view) {
  for (const button of document.querySelectorAll('#cal-views .seg-btn')) {
    const selected = button.dataset.view === view;
    button.classList.toggle('is-on', selected);
    button.setAttribute('aria-selected', String(selected));
  }
}

/**
 * Open the screen. Fetching only happens once Google has been connected.
 *
 * A browser that has connected before gets the calendar back without being
 * asked again: `prompt: ''` is answered silently while the consent is on
 * record, so the Connect button after every reload was a round trip's worth of
 * friction rather than a decision anyone was making. Nothing was stored to
 * achieve it -- one bit saying a connection happened, and Google still holds
 * the only thing that grants access. See reconnect.js.
 *
 * The silent attempt is allowed to fail quietly. Consent does get withdrawn,
 * and the honest answer then is the Connect button, not a red line about it.
 */
export async function openCalendar() {
  ui.showView('calendar');

  if (!calendarAccess?.connected && calendarAccess?.configured && calendarAccess?.remembered) {
    showConnected(false);
    calStatus('Reconnecting to Google…', null);
    const back = await calendarAccess.reconnect();
    calStatus('', null);
    if (!back) {
      // The consent is gone, so stop claiming it every time this screen opens.
      calendarAccess.revoke();
      paintConnectPanel();
    }
  }

  showConnected(Boolean(calendarAccess?.connected));
  if (calendarAccess?.connected) refreshCalendar(calendarView);
}

export function setCalendarView(view) {
  if (view !== 'day' && view !== 'week') return;
  calendarView = view;
  markCalendarView(view);
  refreshCalendar(view);
}

/** The connect button: the one place a consent dialog is worth showing. */
export async function connectCalendar() {
  if (!calendarAccess?.configured) return;
  const button = ui.el('cal-connect-btn');
  button.disabled = true;
  connectError('');
  try {
    await calendarAccess.getToken(true);
    // Remembered only after Google actually said yes, so a cancelled dialog
    // does not leave this browser trying to reconnect to a consent that was
    // never given.
    calendarAccess.remember();
    showConnected(true);
    await refreshCalendar(calendarView);
  } catch (error) {
    connectError(error?.message || 'Could not connect to Google.');
  } finally {
    button.disabled = Boolean(!calendarAccess?.configured);
  }
}

/**
 * Hand the token back to Google and empty the screen.
 *
 * Nothing was kept anywhere else, so this is the whole of it: the next person
 * at this browser gets the connect button, not somebody's week.
 */
export function disconnectCalendar() {
  calendarRequestId += 1;
  calendarAccess?.revoke();
  showConnected(false);
  ui.el('cal-days').replaceChildren();
  ui.el('cal-sources').replaceChildren();
  calStatus('', null);
  calendarView = 'day';
  markCalendarView(calendarView);
  paintConnectPanel();
}

/**
 * One expired token is not a disconnection. The store treats a token as gone a
 * minute early, but a request can still meet a 401 — a revoked grant, a clock
 * that drifted — so the token is dropped and asked for again without a dialog.
 * Only if that silent ask fails does the screen say the connection has gone.
 */
async function readCalendar(range) {
  const token = await calendarAccess.getToken(false);
  try {
    return await loadAllEvents(token, range);
  } catch (error) {
    if (!error?.needsConsent) throw error;
    calendarAccess.forget();
    return loadAllEvents(await calendarAccess.getToken(false), range);
  }
}

export async function refreshCalendar(view = calendarView) {
  if (!calendarAccess?.connected) return;

  const requestId = ++calendarRequestId;
  const range = windowFor(view, new Date());

  let result;
  try {
    result = await readCalendar(range);
  } catch (error) {
    if (requestId !== calendarRequestId) return; // superseded, or disconnected
    if (error?.needsConsent) {
      // Said on the screen they are already looking at, with the button that
      // actually fixes it -- not a retry that cannot work.
      calendarAccess.forget();
      showConnected(false);
      connectError('Your Google connection has expired. Connect again.');
      return;
    }
    ui.el('cal-days').replaceChildren();
    ui.el('cal-sources').replaceChildren();
    calStatus(error?.message || 'Could not reach Google Calendar.', 'error');
    return;
  }

  if (requestId !== calendarRequestId) return;
  calendarBlocks = indexByEvent(await blocksIn(range));
  if (requestId !== calendarRequestId) return;
  renderCalendarSources(result.calendars, result.failed);
  renderCalendarDays(groupEvents(result.events, range, new Date()), view, result.events.length);
}

/**
 * The study blocks Alter published for the days this view covers.
 *
 * Never throws: a plan that cannot be read costs the taps, not the calendar.
 * The days are read in parallel and a day with no plan is simply absent,
 * which is most days.
 */
async function blocksIn(range) {
  if (!todaySource) return [];
  // `windowFor` already names the days it covers, as local `YYYY-MM-DD` --
  // the same strings the plan is filed under. Deriving them again from a
  // start and an end would be a second definition of "which days is this",
  // and the first version of this did exactly that against fields
  // `windowFor` does not return, so nothing was ever tappable.
  const days = range?.days ?? [];
  try {
    return await loadDayPlans(todaySource.config, todaySource.session, days);
  } catch {
    return [];
  }
}

/**
 * Which calendars this is showing, and which refused.
 *
 * Named rather than counted: "the timetable could not be read" is a different
 * day from "you have nothing on", and the desktop app spent an evening
 * confusing the two.
 */
function renderCalendarSources(calendars, failed) {
  const host = ui.el('cal-sources');
  host.replaceChildren();

  for (const calendar of calendars) {
    if (failed.some((f) => f.id === calendar.id)) continue;
    const chip = ui.clone('tpl-cal-chip');
    chip.querySelector('.nav-dot').style.background = calendar.colour || 'var(--faint)';
    chip.querySelector('.cal-chip-name').textContent = calendar.name;
    host.append(chip);
  }

  if (failed.length === 0) {
    calStatus('', null);
    return;
  }
  const named = failed.map((f) => `${f.name} (${f.reason})`).join(', ');
  calStatus(
    failed.length === 1
      ? `One calendar could not be read: ${named}. Everything below is from the rest.`
      : `${failed.length} calendars could not be read: ${named}. Everything below is from the rest.`,
    'error',
  );
}

function renderCalendarDays(days, view, total) {
  const host = ui.el('cal-days');
  host.replaceChildren();
  host.classList.toggle('is-week', view === 'week');

  for (const day of days) {
    const block = ui.clone('tpl-cal-day');
    block.querySelector('.cal-day-label').textContent = day.label;
    block.querySelector('.cal-day-count').textContent =
      day.items.length === 1 ? '1 event' : `${day.items.length} events`;
    block.querySelector('.cal-none').hidden = day.items.length > 0;

    const list = block.querySelector('.cal-list');
    for (const event of day.items) {
      // Only Alter's own study blocks are tappable. Everything else stays the
      // inert row it has always been, so nothing on a read-only screen looks
      // like it could change a calendar.
      const planned = calendarBlocks.get(event.id);
      const row = ui.clone(planned ? 'tpl-cal-study' : 'tpl-cal-event');
      row.classList.toggle('is-all-day', event.allDay);
      row.querySelector('.cal-time-start').textContent = startLabel(event);
      row.querySelector('.cal-time-end').textContent = endLabel(event);
      row.querySelector('.cal-bar').style.background = event.colour || 'var(--accent)';
      row.querySelector('.cal-title').textContent = event.title;
      row.querySelector('.cal-source').textContent = event.calendarName;

      if (planned) {
        const start = row.querySelector('.cal-study-start');
        // Carried on the element rather than in a closure: these rows are
        // rebuilt on every calendar refresh and the click is delegated.
        start.dataset.workItem = planned.workItemId == null ? '' : String(planned.workItemId);
        start.dataset.reason = planned.title || event.title;
        start.dataset.courseCode = planned.courseCode || '';
        start.setAttribute('aria-label', `Start a timer for ${planned.title || event.title}`);
      }
      list.append(row);
    }
    host.append(block);
  }

  if (total === 0 && days.every((day) => day.items.length === 0)) {
    const empty = document.createElement('p');
    empty.className = 'empty';
    empty.textContent = CALENDAR_EMPTY[view] ?? CALENDAR_EMPTY.day;
    host.replaceChildren(empty);
  }
}

/**
 * A tap on a study block: open the timer knowing which assignment it is for.
 *
 * Ready rather than running -- see `timerScreenState`. The work item is sent
 * as the desktop spells it (a bare id), which `keyForSession` turns back into
 * the `w118` key today's list ticks off.
 */
export function onCalendarBlockTap(event) {
  const button = event.target.closest('.cal-study-start');
  if (!button) return;
  const workItem = button.dataset.workItem;
  openTimerFor({
    workItemId: workItem === '' ? null : workItem,
    reason: button.dataset.reason || 'Study',
    courseCode: button.dataset.courseCode || '',
  });
}

/* ── notes: one class ──────────────────────────────────────── */

export function openClass(index) {
  const cls = CLASSES[index];
  // The sidebar is rebuilt whenever Canvas answers, so an index can outlive
  // the class it pointed at -- a stale tap, or a course that vanished between
  // refreshes. Doing nothing beats throwing on `cls.color`.
  if (!cls) return;
  classIndex = index;

  ui.el('class-dot').style.background = cls.color;
  ui.el('class-name').textContent = cls.name;
  // It really is from Canvas now, so it says so. It said "sample data" for as
  // long as that was the truth.
  ui.el('class-meta').textContent = cls.meta ? `${cls.meta} · from Canvas` : 'from Canvas';

  renderNotes(cls.notes);
  renderSets(cls.sets);
  ui.showView('class', `class-${index}`);
}

function renderNotes(notes) {
  const list = ui.el('notes-list');
  list.replaceChildren();
  ui.el('notes-empty').hidden = notes.length > 0;

  notes.forEach((note, index) => {
    const row = ui.clone('tpl-note-row');
    row.querySelector('.note-row-title').textContent = note.title;
    row.querySelector('.note-row-meta').textContent = note.edited
      ? `${note.meta} · edited`
      : note.meta;
    row.querySelector('.note-row').addEventListener('click', () => openNote(index));
    list.append(row);
  });
}

/* ── one note, as a page ───────────────────────────────────── */

export function openNote(index) {
  noteIndex = index;
  const note = currentClass().notes[noteIndex];

  ui.el('notepage-title').value = note.title;
  ui.el('notepage-body').value = note.body ?? '';
  ui.el('notepage-meta').textContent = `${currentClass().name} · ${note.meta}`;
  markSaveState(Boolean(note.edited));

  ui.showView('notepage', `class-${classIndex}`);
}

export function backToClassFromNote() {
  openClass(classIndex);
}

/**
 * Typing writes straight back onto the sample note, so leaving the page and
 * coming back shows what you typed. That is the whole of the persistence:
 * the object lives in the bundle, so a reload restores the original text.
 */
export function onNoteEdited() {
  const note = currentClass().notes[noteIndex];
  note.title = ui.el('notepage-title').value;
  note.body = ui.el('notepage-body').value;
  note.edited = true;
  markSaveState(true);
}

function markSaveState(edited) {
  const state = ui.el('notepage-state');
  state.textContent = edited ? 'Edited · not saved' : 'Not saved';
  state.classList.toggle('is-edited', edited);
}

function renderSets(sets) {
  const list = ui.el('sets-list');
  list.replaceChildren();
  ui.el('sets-empty').hidden = sets.length > 0;

  sets.forEach((set, index) => {
    const row = ui.clone('tpl-set-row');
    row.querySelector('.set-row-name').textContent = set.name;
    row.querySelector('.set-row-count').textContent =
      `${set.cards.length} ${set.cards.length === 1 ? 'card' : 'cards'}`;
    row.addEventListener('click', () => openSet(index));
    list.append(row);
  });
}

/* ── flashcards ────────────────────────────────────────────── */

export function openSet(index) {
  setIndex = index;
  session = study.createSession(currentSet().cards.length);
  drag.active = false;
  drag.leaving = false;
  restCard();
  render();
  ui.showView('study', `class-${classIndex}`);
}

export function backToClass() {
  openClass(classIndex);
}

/** Every control funnels through here: change the session, then redraw. */
function apply(next) {
  if (!session) return;
  session = next(session);
  render();
}

/** Shuffle, review-forgot and restart all deal a fresh card into place. */
function reset(next) {
  if (drag.leaving) return;
  restCard({ entering: true });
  apply(next);
}

export const flip = () => apply(study.flip);
export const shuffle = () => reset((s) => study.shuffle(s));
export const reviewForgot = () => reset(study.reviewForgot);
export const restart = () => reset(() => study.createSession(currentSet().cards.length));

/* ── the swipe ─────────────────────────────────────────────── */

/**
 * Scoring a card always throws it off screen first, whether that came from a
 * drag or from the Forgot/Known buttons — one exit, so the buttons teach the
 * gesture. Input is ignored until the card has gone, which is also what stops
 * a hammered button from scoring two cards on one animation.
 */
export function markKnown() { throwCard(true); }
export function markForgot() { throwCard(false); }

function throwCard(known) {
  if (!session || session.done || drag.leaving || session.order.length === 0) return;

  drag.leaving = true;
  const swipe = ui.el('card-swipe');
  swipe.classList.remove('is-dragging');
  swipe.classList.add('is-leaving');

  if (reducedMotion()) {
    swipe.style.opacity = '0';
  } else {
    // Off the viewport edge whatever the card's width, still leaning.
    const distance = (known ? 1 : -1) * (window.innerWidth || 900);
    swipe.style.transform = `translateX(${distance}px) rotate(${distance * 0.01}deg)`;
    swipe.style.opacity = '0';
  }

  window.setTimeout(() => {
    drag.leaving = false;
    restCard({ entering: true });
    apply((s) => study.mark(s, known));
  }, reducedMotion() ? 150 : LEAVE_MS);
}

/** Put the card back at rest, optionally letting the next one arrive. */
function restCard({ entering = false } = {}) {
  const swipe = ui.el('card-swipe');
  swipe.classList.remove('is-leaving', 'is-dragging', 'is-entering');
  swipe.style.transform = '';
  swipe.style.opacity = '';
  paintVerdict(0);

  if (!entering || reducedMotion()) return;
  // Reading offsetWidth restarts the keyframes; without it the class goes on
  // and off within one frame and nothing plays.
  void swipe.offsetWidth;
  swipe.classList.add('is-entering');
}

export function onCardPointerDown(event) {
  if (!session || session.done || drag.leaving) return;
  drag.active = true;
  drag.startX = event.clientX;
  drag.dx = 0;

  const swipe = ui.el('card-swipe');
  swipe.classList.add('is-dragging'); // no transition: the card sits on the finger
  swipe.classList.remove('is-entering');
  // Keep receiving moves even when the pointer leaves the card.
  event.currentTarget.setPointerCapture?.(event.pointerId);
}

export function onCardPointerMove(event) {
  if (!drag.active) return;
  drag.dx = event.clientX - drag.startX;

  const swipe = ui.el('card-swipe');
  swipe.style.transform = reducedMotion()
    ? `translateX(${drag.dx}px)`
    : `translateX(${drag.dx}px) rotate(${drag.dx * TILT_DEG_PER_PX}deg)`;
  paintVerdict(drag.dx);
}

export function onCardPointerUp(event) {
  if (!drag.active) return;
  drag.active = false;
  event.currentTarget.releasePointerCapture?.(event.pointerId);

  const { dx } = drag;
  const swipe = ui.el('card-swipe');
  swipe.classList.remove('is-dragging'); // hands the card back to the transition

  if (Math.abs(dx) > SWIPE_PX) return throwCard(dx > 0);

  restCard(); // springs back from wherever it was let go
  if (Math.abs(dx) < TAP_PX) flip();
}

/** A drag cancelled by the system — a phone call, a gesture the OS took. */
export function onCardPointerCancel() {
  if (!drag.active) return;
  drag.active = false;
  restCard();
}

function paintVerdict(dx) {
  const lit = Math.min(1, Math.abs(dx) / SWIPE_PX);
  ui.el('verdict-known').style.opacity = dx > 0 ? String(lit) : '0';
  ui.el('verdict-forgot').style.opacity = dx < 0 ? String(lit) : '0';
}

export function onCardKeyDown(event) {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault(); // Space would otherwise scroll the page
  flip();
}

function render() {
  const set = currentSet();
  ui.el('set-name').textContent = set.name;
  ui.el('set-class').textContent = currentClass().name;

  ui.el('study-active').hidden = session.done;
  ui.el('study-done').hidden = !session.done;

  if (session.done) renderDone();
  else renderCard(set);
}

function renderCard(set) {
  const { known, forgot } = study.counts(session);
  const index = study.currentIndex(session);
  const [term, definition] = index === null ? ['No cards', ''] : set.cards[index];

  ui.el('study-progress-bar').style.width = `${study.progressPercent(session)}%`;
  ui.el('count-forgot').textContent = `Forgot ${forgot}`;
  ui.el('count-known').textContent = `Known ${known}`;
  ui.el('count-position').textContent = study.position(session);

  ui.el('card-term').textContent = term;
  ui.el('card-definition').textContent = definition;
  ui.el('card').classList.toggle('is-flipped', session.flipped);

  // Announce whichever face is now showing — the flip is invisible otherwise.
  ui.el('card-reader').textContent = session.flipped
    ? `Definition: ${definition}`
    : `Term: ${term}`;
}

function renderDone() {
  const { known, forgot } = study.counts(session);
  ui.el('done-known').textContent = String(known);
  ui.el('done-forgot').textContent = String(forgot);
  ui.el('done-line').textContent = `${known} known · ${forgot} forgot`;

  const review = ui.el('review-forgot');
  review.hidden = forgot === 0;
  review.textContent = `Review ${forgot} forgot`;
  ui.el('done-back').textContent = `Back to ${currentClass().name}`;
}
